import { useContractDetailsPopup } from 'features/ContractDetails/Providers/ContractDetailsPopupProvider'

import { useCallback, useState } from 'react'
import { TPreviewDocument, TUploadAutoContractDocumentsForm } from './types'
import moment from 'moment'
import { FormInstance } from 'antd'
import { useDispatch } from 'react-redux'
import { setLoading } from 'redux/store/common/slice'
import { DOCUMENT_TYPE } from 'components/FileUpload/constants'
import { useContractDetailsContext } from 'features/ContractDetails/Providers/ContractDetailsContextProvider'
import { formatPhoneNumberToRequest } from 'helper/common'
import { removeItemByIndex } from './helpers'
import { TContractUploadFile } from 'features/ContractDetails/types'
import { notification } from 'components/Notification'
import { AxiosResponse } from 'axios'
import { VALIDATION_MESSAGES } from 'constants/txt'
import { MAX_CONTRACT_DOCUMENTS } from '../constants'

export const useUploadAutoContractDocumentsPopup = () => {
  const {
    uploadAutoContractDocumentPopup: { actions, state }
  } = useContractDetailsPopup()

  const {
    actions: {
      handleAttachContractDocumentsIdsAsync,
      handleUploadContractDocumentToTempStoreAsync
    }
  } = useContractDetailsContext()

  const dispatch = useDispatch()

  const [forms, setForms] = useState<
    FormInstance<TUploadAutoContractDocumentsForm>[]
  >([])
  const [documents, setDocuments] = useState<TPreviewDocument[]>([])

  const uploadedDocumentsCount = state.payload?.uploadedDocumentsCount ?? 0
  const isUploadLimitReached =
    uploadedDocumentsCount + documents.length >= MAX_CONTRACT_DOCUMENTS

  const registerForm = useCallback(
    (form: FormInstance<TUploadAutoContractDocumentsForm>) => {
      setForms((prev) => [...prev, form])
    },
    []
  )

  const onClose = useCallback(() => {
    actions.close()
    setDocuments([])
    setForms([])
  }, [actions])

  const handleDeleteDocument = useCallback((index: number) => {
    setDocuments((prev) => removeItemByIndex(prev, index))
    setForms((prev) => removeItemByIndex(prev, index))
  }, [])

  const handleUploadDocument = useCallback(
    async (file: File[]) => {
      if (isUploadLimitReached) return

      try {
        dispatch(setLoading(true))
        const tempStoreResult =
          await handleUploadContractDocumentToTempStoreAsync(
            file[0],
            DOCUMENT_TYPE.TEMPLATE
          )
        if (!tempStoreResult || !tempStoreResult?.uuid) {
          throw new Error('Failed to upload document to temp store')
        }
        setDocuments((prev) => [
          ...prev,
          {
            file: file[0],
            uuid: tempStoreResult?.uuid,
            uploadedAt: moment()
          }
        ])
      } catch {
        return
      } finally {
        dispatch(setLoading(false))
      }
    },
    [
      handleUploadContractDocumentToTempStoreAsync,
      dispatch,
      isUploadLimitReached
    ]
  )

  const onAddDocument = useCallback(async () => {
    if (!documents.length) return

    // validate forms
    try {
      await Promise.all(
        forms.filter(Boolean).map((form) => form.validateFields())
      )
    } catch {
      return
    }

    // prepare data for api call
    const data: TContractUploadFile['files'] = documents.map(
      (document, idx) => {
        const parsedData: TContractUploadFile['files'][0] = {
          uploaded_file: document.uuid
        }
        const form = forms[idx]
        const redliningSwitch = form?.getFieldValue('redliningSwitch')
        const signatorySwitch = form?.getFieldValue('signatorySwitch')
        const signatory = form?.getFieldValue('signatory')
        const phoneNumber = form?.getFieldValue('phoneNumber')

        if (redliningSwitch) {
          parsedData.is_redlining_required = redliningSwitch
        }

        if (signatory && signatorySwitch) {
          parsedData.is_digital_signature_required = signatorySwitch
          parsedData.signatory = signatory
          if (phoneNumber) {
            parsedData.phone_number =
              formatPhoneNumberToRequest(phoneNumber) ?? undefined
          }
        }
        return parsedData
      }
    )

    try {
      dispatch(setLoading(true))
      await handleAttachContractDocumentsIdsAsync(data)
      notification.success({
        message: VALIDATION_MESSAGES.SM0044
      })
      setDocuments([])
      setForms([])
      actions.close()
    } catch (error) {
      const { status, data } = error as AxiosResponse
      if (status === 400 && data?.non_field_errors?.length) {
        notification.error({
          message: data.non_field_errors[0]
        })
      }
    } finally {
      dispatch(setLoading(false))
    }
  }, [
    documents,
    dispatch,
    handleAttachContractDocumentsIdsAsync,
    forms,
    actions
  ])

  return {
    forms,
    documents,
    onClose,
    handleDeleteDocument,
    handleUploadDocument,
    onAddDocument,
    registerForm,
    isUploadLimitReached
  }
}
